import { useEffect, useState } from "react";
import { ConfirmHost } from "./components/confirm";
import { BrowserRouter, NavLink, Outlet, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { ListenBook, ListenLibrary } from "./pages/Listen";
import { ListenDrawer } from "./components/ListenDrawer";
import { useQuery } from "@tanstack/react-query";
import { UserButton } from "@clerk/clerk-react";
import { api } from "./api/client";
import type { ModuleManifest, Project } from "./api/types";
import { Icon } from "./components/icons";
import { ProjectList } from "./pages/ProjectList";
import { NewProject } from "./pages/NewProject";
import { ProjectWorkspace } from "./pages/ProjectWorkspace";
import { ReportViewer } from "./pages/ReportViewer";
import { ManuscriptReview } from "./pages/ManuscriptReview";
import { SpecCheck } from "./pages/SpecCheck";
import TableOfContents from "./pages/TableOfContents";
import { Workflows } from "./pages/Workflows";
import { MyWork } from "./pages/MyWork";
import { Proofers, ProoferLog } from "./pages/Proofers";
import { TeamSettings } from "./pages/TeamSettings";
import { ClaimInvite } from "./pages/ClaimInvite";
import { PickupsPackage } from "./pages/PickupsPackage";
import { SharedPackage } from "./pages/SharedPackage";
import { SharedGuide } from "./pages/SharedGuide";
import { SharedManuscript } from "./pages/SharedManuscript";
import { ProjectGuide } from "./pages/ProjectGuide";
import { ComingSoon } from "./pages/ComingSoon";
import { AdminActivity } from "./pages/AdminActivity";
import { AdminReleases } from "./pages/AdminReleases";
import { AdminCosts } from "./pages/AdminCosts";
import { AdminTransactions } from "./pages/AdminTransactions";
import { AdminSwitches } from "./pages/AdminSwitches";
import { NoiseLab } from "./pages/NoiseLab";
import { NoiseLabSet } from "./pages/NoiseLabSet";
import { NoiseLabReview } from "./pages/NoiseLabReview";
import { NoiseLabModels } from "./pages/NoiseLabModels";
import { NoiseLabModel } from "./pages/NoiseLabModel";
import { NoiseAnalytics } from "./pages/NoiseAnalytics";
import { NoiseReview } from "./pages/NoiseReview";
import { NotificationSettings } from "./components/NotificationSettings";
import { useMe } from "./lib/me";
import { UploadProvider } from "./lib/uploads";
import { ReplaceJobsProvider, useReplaceJobs } from "./lib/replaceJobs";
import { ReplaceJobDialog, ReplaceJobPills } from "./components/ReplaceJobDialog";
import { HistoryDrawer } from "./components/HistoryDrawer";
import { UpdateNotice } from "./components/UpdateNotice";
import { useEnvironment } from "./lib/environment";
import { useActiveWork } from "./lib/history";
import { useUploads } from "./lib/uploads";
import { Toaster } from "./components/toast";

function useModules() {
  return useQuery({
    queryKey: ["modules"],
    queryFn: () => api<{ modules: ModuleManifest[] }>("/api/modules").then((r) => r.modules),
    staleTime: Infinity,
  });
}

// True only in cloud builds (Clerk key baked in). Gates the sign-out control so
// it's never rendered — nor a Clerk hook invoked — in the local auth-free build.
const AUTH_ON = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

function Sidebar({ collapsed, onToggle, onListen, onHistory, histOpen }: {
  collapsed: boolean; onToggle: () => void; onListen: () => void;
  onHistory: () => void; histOpen: boolean;
}) {
  const { data: modules } = useModules();
  const { data: me } = useMe();
  // Both halves of "something is happening": server jobs and this browser's
  // uploads. The badge is the only live signal left now the tray has gone, so
  // it has to count both or work could still run unseen.
  const { jobCount } = useActiveWork();
  const { activeCount } = useUploads();
  const { runningCount } = useReplaceJobs();
  const busyCount = jobCount + activeCount + runningCount;
  // Shares the ["projects","active"] cache with ProjectList, so the per-workflow
  // counts stay in lockstep with the lists they point at.
  const { data: projects } = useQuery({
    queryKey: ["projects", "active"],
    queryFn: () => api<{ projects: Project[] }>("/api/projects").then((r) => r.projects),
    // Deliberately NOT polled. The sidebar lives on every screen, so an interval
    // here re-downloaded the whole project list (172 KB) every 15s no matter what
    // you were doing — our single biggest source of egress, to keep a few counts
    // fresh. Everything that changes those counts already invalidates
    // ["projects"]: create, archive, delete, transfer, upload, workflow toggle —
    // plus useActiveWork(), which invalidates when a background job ends (the
    // one change with no local mutation to hang it off — it used to live in the
    // activity tray, and moved here when that was replaced by History). ProjectList keeps its own
    // interval, so the list still refreshes while you're looking at it.
  });
  const countFor = (moduleId: string) =>
    (projects || []).filter((p) => (p.workflows as any)?.[moduleId]).length;
  // Roles are a set: an admin can also proof. A proofer-only account sees only
  // "My Work"; an admin sees the full producer nav (+ "My Work" if they proof too).
  const isAdmin = !!me?.is_superadmin || !!me?.roles?.includes("admin");
  const isProofer = !!me?.entitlements?.proofer;
  const env = useEnvironment();
  return (
    <aside className={`sidebar${collapsed ? " collapsed" : ""}`}>
      <div className="brand-row">
        <NavLink to="/" className="brand" title="Mockingbird">
          <Icon name="logo" size={28} />
          <span className="wm">Mockingbird</span>
        </NavLink>
        <button className="nav-collapse-btn" onClick={onToggle} title={collapsed ? "Expand sidebar" : "Collapse sidebar"}>
          <Icon name={collapsed ? "chevron-right" : "chevron-left"} size={16} />
        </button>
      </div>
      {env !== "production" && (
        <div className="env-badge" title="A test copy of Mockingbird: nothing here touches the live app">
          {env} · test copy
        </div>
      )}
      <nav>
        {isAdmin && (
          <>
            {me?.entitlements?.workflows && (
              <NavLink to="/workflows" className="nav-item" title="Workflows — production board">
                <Icon name="workflows" size={18} />
                <span>Workflows</span>
              </NavLink>
            )}
            <NavLink to="/" end className="nav-item" title="Projects">
              <Icon name="folder" size={18} />
              <span>Projects</span>
            </NavLink>
            <NavLink to="/proofers" className="nav-item" title="Proofers — roster + their logs">
              <Icon name="headphones" size={18} />
              <span>Proofers</span>
            </NavLink>
          </>
        )}
        {/* Last in the top group rather than first: an admin who also proofs
            spends the day on the board, not in their own queue. It sits OUTSIDE
            the isAdmin block because a proofer-only account has this as its only
            nav item. */}
        {isProofer && (
          <NavLink to="/my-work" className="nav-item" title="My Work — the jobs assigned to you">
            <Icon name="headphones" size={18} />
            <span>My Work</span>
          </NavLink>
        )}
        {/* The phone surface. It lives OUTSIDE the app shell, so without an
            entry here the only way in is to type the URL — which is how it
            shipped, and why nobody could find it. */}
        <button className="nav-item" onClick={onListen}
          title="Listen — play your books without leaving what you're doing">
          <Icon name="play" size={18} />
          <span>Listen</span>
        </button>
        {/* Under Listen, as its own surface rather than a toast: the record of
            what the tool has done is worth opening deliberately, and the corner
            tray could only ever be seen while something was still running. The
            badge is what says there is something live to look at. */}
        <button className={`nav-item${histOpen ? " on" : ""}`} onClick={onHistory}
          title="History — everything this account has been put through">
          <Icon name="repeat" size={18} />
          <span>History</span>
          {busyCount > 0 && <span className="nav-count live">{busyCount}</span>}
        </button>
        {isAdmin && (
          <>
            <div className="nav-section">Modules</div>
            {(modules || []).map((m) =>
              m.status === "active" ? (
                <NavLink key={m.id} to={`/workflows/${m.id}`} className="nav-item" title={m.description}>
                  <Icon name={m.icon} size={18} />
                  <span>{m.label}</span>
                  {countFor(m.id) > 0 && <span className="nav-count">{countFor(m.id)}</span>}
                </NavLink>
              ) : (
                <NavLink
                  key={m.id}
                  to={`/modules/${m.id}`}
                  className="nav-item soon"
                  title={m.description}
                >
                  <Icon name={m.icon} size={18} />
                  <span>{m.label}</span>
                  <span className="tag">soon</span>
                </NavLink>
              )
            )}
          </>
        )}
        {isAdmin && (
          <>
            <div className="nav-section">Settings</div>
            <NavLink to="/settings/team" className="nav-item" title="Team — invite people and set their roles">
              <Icon name="settings" size={18} />
              <span>Team</span>
            </NavLink>
          </>
        )}
        {me?.is_superadmin && (
          <>
            <div className="nav-section">Admin</div>
            <NavLink to="/admin/activity" className="nav-item" title="Activity dashboard (superadmin)">
              <Icon name="grid" size={18} />
              <span>Activity</span>
            </NavLink>
            <NavLink to="/admin/releases" className="nav-item" title="Releases: put changes live (superadmin)">
              <Icon name="upload" size={18} />
              <span>Releases</span>
            </NavLink>
            <NavLink to="/admin/costs" className="nav-item" title="Costs & P&L (superadmin)">
              <Icon name="receipt" size={18} />
              <span>Costs &amp; P&amp;L</span>
            </NavLink>
            <NavLink to="/admin/transactions" className="nav-item" title="Transaction cost log (superadmin)">
              <Icon name="list" size={18} />
              <span>Transactions</span>
            </NavLink>
            <NavLink to="/admin/switches" className="nav-item" title="Feature switches: who sees each new feature (superadmin)">
              <Icon name="toggle" size={18} />
              <span>Feature switches</span>
            </NavLink>
            <NavLink to="/admin/noise-lab" className="nav-item" title="Noise Lab: training data for noise detection (superadmin)">
              <Icon name="flask" size={18} />
              <span>Noise Lab</span>
            </NavLink>
          </>
        )}
      </nav>
      <div className="sidebar-foot">
        {AUTH_ON && (
          <div className="sidebar-user">
            <UserButton afterSignOutUrl="/">
              {/* A custom "Notifications" page inside Clerk's Manage-account
                  panel — where the user sets their notification email. */}
              <UserButton.UserProfilePage
                label="Notifications"
                url="notifications"
                labelIcon={<Icon name="bell" size={16} />}
              >
                <NotificationSettings />
              </UserButton.UserProfilePage>
            </UserButton>
            <span className="muted">Account</span>
          </div>
        )}
        <span className="muted foot-made">
          Made in London, New York City and Los Angeles with passion for storytelling
          {" "}<span className="foot-heart" aria-hidden="true">♥</span>
          {" "}· v0.1
        </span>
      </div>
    </aside>
  );
}

function Layout() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("mb.nav") === "1");
  const [histOpen, setHistOpen] = useState(false);
  useEffect(() => { localStorage.setItem("mb.nav", collapsed ? "1" : "0"); }, [collapsed]);
  // Opened at least once ⇒ stays mounted, so closing the drawer hides it
  // WITHOUT unmounting the <audio> inside and stopping playback mid-sentence.
  const [listenOpen, setListenOpen] = useState(false);
  const [listenUsed, setListenUsed] = useState(false);
  const navigate = useNavigate();
  const openListen = () => {
    // On a narrow screen the drawer would be the whole viewport, so the
    // standalone route is simply the better version of the same thing.
    if (window.matchMedia("(max-width: 760px)").matches) { navigate("/listen"); return; }
    setListenUsed(true);
    setListenOpen((v) => !v);
  };
  return (
    <div className={`shell${collapsed ? " nav-collapsed" : ""}${listenOpen ? " listen-open" : ""}`}>
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} onListen={openListen}
        onHistory={() => setHistOpen(true)} histOpen={histOpen} />
      <main className="main">
        <Outlet />
      </main>
      {listenUsed && <ListenDrawer open={listenOpen} onClose={() => setListenOpen(false)} />}
      <Toaster />
      <ConfirmHost />
      <UpdateNotice />
      <ReplaceJobPills />
      <ReplaceJobDialog />
      <HistoryDrawer open={histOpen} onClose={() => setHistOpen(false)} />
    </div>
  );
}

function ComingSoonRoute() {
  const { moduleId } = useParams();
  return <ComingSoon moduleId={moduleId!} />;
}

// A workflow's filtered project list (sidebar → Proofing / Spec check).
// The report holds ONE project's review state (dismissals, comments, manual
// flags) from the moment it opens. Switching books in the header keeps the same
// route, so without a key React kept the report - and the last book's state,
// which was then saved as this book's (Oct 2026: another book's manual flags in
// Sleep Almanac's Pickups Package). A new book opens a new report.
function ReportRoute() {
  const { projectId } = useParams();
  return <ReportViewer key={projectId} />;
}

function WorkflowRoute() {
  const { moduleId } = useParams();
  return <ProjectList workflow={moduleId} />;
}

// The home screen depends on the caller: a proofer-only account lands on My Work;
// everyone else (admins, local dev) gets the project list.
function HomeRoute() {
  const { data: me } = useMe();
  const isAdmin = !!me?.is_superadmin || !!me?.roles?.includes("admin");
  if (me?.entitlements?.proofer && !isAdmin) return <MyWork />;
  // Signed in but holding NO role — almost always an invite not yet linked to
  // this account. Say so, rather than showing an empty project list that reads
  // as "the app is broken".
  if (me && !isAdmin && !me.entitlements?.proofer) {
    return (
      <div className="pl-page">
        <header className="pl-head">
          <h1>You're signed in</h1>
          <p className="muted">
            Your account isn't linked to an invitation yet, so there's no work to show here.
          </p>
        </header>
        <div className="pl-empty">{me.member_hint ?? "Ask your admin to check your invite."}</div>
      </div>
    );
  }
  return <ProjectList />;
}

export function App() {
  return (
    <BrowserRouter>
      <UploadProvider>
      <ReplaceJobsProvider>
      <Routes>
        <Route element={<Layout />}>
          <Route index element={<HomeRoute />} />
          <Route path="my-work" element={<MyWork />} />
          <Route path="claim/:token" element={<ClaimInvite />} />
          <Route path="proofers" element={<Proofers />} />
          <Route path="proofers/:memberId" element={<ProoferLog />} />
          <Route path="settings/team" element={<TeamSettings />} />
          <Route path="workflows" element={<Workflows />} />
          <Route path="new" element={<NewProject />} />
          <Route path="projects/:projectId" element={<ProjectWorkspace />} />
          <Route path="projects/:projectId/manuscript" element={<ManuscriptReview />} />
          <Route path="projects/:projectId/specs" element={<SpecCheck />} />
          <Route path="projects/:projectId/toc" element={<TableOfContents />} />
          <Route path="projects/:projectId/noise" element={<NoiseAnalytics />} />
          <Route path="projects/:projectId/noise/:chapterId" element={<NoiseReview />} />
          <Route path="projects/:projectId/report" element={<ReportRoute />} />
          <Route path="projects/:projectId/report/:chapterId" element={<ReportRoute />} />
          <Route path="workflows/:moduleId" element={<WorkflowRoute />} />
          <Route path="modules/:moduleId" element={<ComingSoonRoute />} />
          <Route path="admin/activity" element={<AdminActivity />} />
          <Route path="admin/releases" element={<AdminReleases />} />
          <Route path="admin/costs" element={<AdminCosts />} />
          <Route path="admin/transactions" element={<AdminTransactions />} />
          <Route path="admin/switches" element={<AdminSwitches />} />
          <Route path="admin/noise-lab" element={<NoiseLab />} />
          <Route path="admin/noise-lab/models" element={<NoiseLabModels />} />
          <Route path="admin/noise-lab/models/:modelId" element={<NoiseLabModel />} />
          <Route path="admin/noise-lab/:setId" element={<NoiseLabSet />} />
          <Route path="admin/noise-lab/:setId/review/:reviewId" element={<NoiseLabReview />} />
          <Route path="*" element={<HomeRoute />} />
        </Route>
        {/* Chrome-less: the Pickups Package is a standalone, shareable page —
            rendered OUTSIDE the app shell (no sidebar) so it can be handed to a
            narrator/editor. */}
        <Route path="projects/:projectId/pickups" element={<PickupsPackage />} />
        {/* The same package regrouped into passages to record again. */}
        <Route path="projects/:projectId/guide" element={<ProjectGuide />} />
        {/* Public, no-login share view — token-scoped, also outside the shell. */}
        <Route path="share/:token" element={<SharedPackage />} />
        <Route path="share/:token/guide" element={<SharedGuide />} />
        {/* The manuscript download link for proof listeners — its own token. */}
        <Route path="share/manuscript/:token" element={<SharedManuscript />} />
        {/* Listening on the phone. Outside the shell because the desktop chrome
            (sidebar, wide tables) is exactly what makes the app unusable on a
            phone — this is a different surface for a different posture, not a
            responsive squeeze of the same one. */}
        <Route path="listen" element={<ListenLibrary />} />
        <Route path="listen/:projectId" element={<ListenBook />} />
      </Routes>
      </ReplaceJobsProvider>
      </UploadProvider>
    </BrowserRouter>
  );
}
